// Chakra imports
import {
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  Flex,
  Box,
  Td,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Select,
  IconButton,
  Alert,
  AlertIcon,
  Checkbox,
  VStack,
  Progress,
  useToast
} from "@chakra-ui/react";
import React, { useState, useEffect, useCallback } from "react";
import { 
  FaDownload, 
  FaChevronLeft, 
  FaChevronRight,
  FaFileExcel,
  FaFileCsv,
  FaFilePdf,
  FaCheck,
  FaClock,
  FaTimes,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSync,
  FaExclamationTriangle,
  FaReceipt
} from "react-icons/fa";

// Componentes
import PaymentTableRow from "components/Tables/PaymentTableRow";
import ModalOrderEdit from "components/Modal/ModalOrderEdit";

// Servicios
import { triggerStatusChange } from "services/billing";
import { deleteOrder } from "services/woo";

// Servicios para exportar morosos
import { exportOverdueToExcel, formatCurrency } from "services/overdueReportsService";

const PaymentTable = ({ 
  payments, 
  filters, 
  pagination, 
  onPaginationChange, 
  onExport,
  isExporting = false,
  onRefresh,
  onSortChange,
  onSync,
  onUpdateInvoiceNumbers,
  isLoading = false
}) => {
  
  const textColor = "ink.900";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOrder, setModalOrder] = useState({
    id: '',
    meta_data: [],
    billing: { first_name: '', last_name: '', email: '' },
    total: '',
    status: '',
  });
  const [modalCustomer, setModalCustomer] = useState({
    first_name: '',
    last_name: '',
  });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();
  
  // Estados para selección múltiple
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllPages, setSelectAllPages] = useState(false); // Nuevo estado para seleccionar todo
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isExportingOverdue, setIsExportingOverdue] = useState(false);
  
  // Estados para progreso en tiempo real
  const [progressData, setProgressData] = useState({
    current: 0,
    total: 0,
    currentOrder: null,
    completed: [],
    errors: [],
    percentage: 0,
    status: 'idle' // idle, processing, completed, failed
  });
  
  // Estados para ordenamiento
  const [sortField, setSortField] = useState(filters.orderby || 'date');
  const [sortOrder, setSortOrder] = useState(filters.order || 'desc');

  // Reset de selección cuando cambian los datos (sin notificar al padre)
  useEffect(() => {
    if (!selectAllPages) {
      setSelectedOrders(new Set());
      setSelectAll(false);
    }
  }, [payments.data, selectAllPages]);
  
  // Sincronizar estados de ordenamiento con filtros
  useEffect(() => {
    if (filters.orderby && filters.orderby !== sortField) {
      setSortField(filters.orderby);
    }
    if (filters.order && filters.order !== sortOrder) {
      setSortOrder(filters.order);
    }
  }, [filters.orderby, filters.order]);


  const handleRowClick = (rowData, event) => {
    // No abrir modal si se hizo click en el checkbox
    if (event && event.target && event.target.type === 'checkbox') {
      return;
    }
    setModalOrder(rowData);
    setModalCustomer({
      first_name: rowData.billing.first_name,
      last_name: rowData.billing.last_name,
    });
    setIsModalOpen(true);
  };

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    onPaginationChange({ ...pagination, page: newPage });
  };

  // Manejar cambio de elementos por página
  const handlePerPageChange = (newPerPage) => {
    onPaginationChange({ ...pagination, page: 1, per_page: parseInt(newPerPage) });
  };
  
  // Manejar cambio de ordenamiento
  const handleSortChange = (field) => {
    let newOrder = 'desc';
    
    // Si es el mismo campo, alternar orden
    if (sortField === field) {
      newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    }
    
    setSortField(field);
    setSortOrder(newOrder);
    
    // Notificar al componente padre
    if (onSortChange) {
      onSortChange(field, newOrder);
    }
  };
  
  // Renderizar icono de ordenamiento
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <Icon as={FaSort} color="ink.400" ml={1} />;
    }
    
    return (
      <Icon
        as={sortOrder === 'desc' ? FaSortDown : FaSortUp}
        color="coral.500"
        ml={1}
      />
    );
  };

  // Manejar exportación
  const handleExport = async (format) => {
    // Si hay órdenes seleccionadas, exportar solo esas
    if (selectedOrders.size > 0) {
      const selectedIds = Array.from(selectedOrders);
      await onExport(format, selectedIds);
    } else {
      // Si no hay selección, exportar todas (comportamiento original)
      await onExport(format);
    }
  };

  // Manejar exportación rápida (hoy/ayer)
  const handleQuickExport = async (period) => {
    try {
      const today = new Date();
      let startDate, endDate;

      if (period === 'today') {
        startDate = endDate = today.toISOString().split('T')[0]; // YYYY-MM-DD
      } else if (period === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0]; // YYYY-MM-DD
      }

      // Crear filtros temporales para la exportación
      const quickExportFilters = {
        ...filters,
        start_date: startDate,
        end_date: endDate,
        status: 'all' // Incluir todos los estados para el período
      };

      // Usar la función onExport con filtros específicos
      await onExport('excel', null, quickExportFilters);
      
      toast({
        title: 'Exportación completada',
        description: `Se han exportado los pagos de ${period === 'today' ? 'hoy' : 'ayer'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error en exportación rápida:', error);
      toast({
        title: 'Error en exportación',
        description: 'No se pudo completar la exportación rápida',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Período efectivo que usará el exportador de morosos
  const getOverduePeriod = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const schoolYear = currentDate.getMonth() < 2 ? currentYear - 1 : currentYear;
    const start = filters.start_date || `${schoolYear}-03-01`;
    const end   = filters.end_date   || `${schoolYear}-12-31`;
    const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
    return { start, end, label: `${fmt(start)} → ${fmt(end)}`, isCustom: !!(filters.start_date || filters.end_date) };
  };

  // Función para exportar morosos
  const handleExportOverdue = async () => {
    try {
      setIsExportingOverdue(true);

      toast({
        title: 'Generando listado de morosos...',
        description: 'Obteniendo todas las mensualidades pendientes del año escolar',
        status: 'info',
        duration: 3000,
        isClosable: true
      });

      // Usar el rango de fechas de los filtros aplicados; si no hay, usar año escolar por defecto
      const getDefaultSchoolYear = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const schoolYear = currentDate.getMonth() < 2 ? currentYear - 1 : currentYear;
        return {
          start: `${schoolYear}-03-01`,
          end: `${schoolYear}-12-31`
        };
      };

      const defaultPeriod = getDefaultSchoolYear();

      const overdueFilters = {
        start_date: filters.start_date || defaultPeriod.start,
        end_date: filters.end_date || defaultPeriod.end,
        min_days_overdue: 0,
        include_critical_only: false,
        status: 'all'
      };

      // Ejecutar exportación
      const result = await exportOverdueToExcel(overdueFilters);

      if (result.success) {
        toast({
          title: '✅ Listado de morosos generado',
          description: `Excel descargado: ${result.recordCount} estudiantes morosos en ${result.gradesCount} cursos. Total adeudado: ${formatCurrency(result.totalAmount)}`,
          status: 'success',
          duration: 8000,
          isClosable: true
        });
      } else {
        throw new Error('Error en la generación del reporte');
      }

    } catch (error) {
      console.error('Error exportando morosos:', error);
      
      toast({
        title: 'Error generando listado',
        description: error.message || 'No se pudo generar el reporte de morosos. Intente nuevamente.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsExportingOverdue(false);
    }
  };

  // Funciones de selección múltiple
  const handleSelectOrder = (orderId, isChecked) => {
    const newSelected = new Set(selectedOrders);
    if (isChecked) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrders(newSelected);
    setSelectAll(newSelected.size === payments.data.length);
    
    // Si hay selección manual, desactivar modo "todas las páginas"
    if (selectAllPages) {
      setSelectAllPages(false);
    }
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allIds = new Set(payments.data.map(order => order.id));
      setSelectedOrders(allIds);
      setSelectAll(true);
      setSelectAllPages(false); // Primero selecciona solo la página actual
    } else {
      const emptySet = new Set();
      setSelectedOrders(emptySet);
      setSelectAll(false);
      setSelectAllPages(false);
    }
  };

  // Nueva función para seleccionar todas las páginas (OPTIMIZADA - sin cargar todas)
  const handleSelectAllPages = async () => {
    if (selectAllPages) {
      // Si ya están todas seleccionadas, deseleccionar todo
      const emptySet = new Set();
      setSelectedOrders(emptySet);
      setSelectAll(false);
      setSelectAllPages(false);
      
      toast({
        title: 'Selección masiva cancelada',
        description: 'Se ha cancelado la selección de todas las órdenes',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } else {
      // Marcar que se usarán TODAS las órdenes del filtro (sin cargarlas)
      setSelectAllPages(true);
      setSelectAll(true);
      
      // LIMPIAR selecciones individuales para evitar confusión en UI
      setSelectedOrders(new Set());
      
      // NO cargar todas las órdenes en memoria
      // Las acciones bulk usarán directamente los filtros aplicados
      
      toast({
        title: '✅ Todas las órdenes del filtro seleccionadas',
        description: `Se procesarán todas las ${payments.total.toLocaleString()} órdenes que coinciden con el filtro actual`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Función para sincronizar órdenes - OPTIMIZADA para selección masiva
  const handleBulkSync = async () => {
    let orderIds = null;
    let description = '';
    let useFilters = false;

    if (selectAllPages) {
      // Modo selección masiva: usar filtros directamente
      useFilters = true;
      description = `todas las ${payments.total.toLocaleString()} órdenes del filtro actual`;
    } else if (selectedOrders.size > 0) {
      // Con selección específica: usar IDs
      orderIds = Array.from(selectedOrders);
      description = `${orderIds.length} orden${orderIds.length !== 1 ? 'es' : ''} seleccionada${orderIds.length !== 1 ? 's' : ''}`;
    } else {
      // Sin selección: todas las del filtro
      useFilters = true;
      description = 'todas las órdenes del filtro actual';
    }

    const confirmMessage = `¿Estás seguro de que quieres sincronizar ${description}?\n\n⚠️ Esto actualizará información del alumno, apoderado y generará boletas faltantes.\n💡 El proceso puede tomar varios minutos para grandes volúmenes.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Ejecutar sincronización 
      if (onSync) {
        if (useFilters) {
          // Pasar filtros en lugar de IDs para procesamiento masivo optimizado
          await onSync(filters, null, true); // true = use filters
        } else {
          // Usar IDs específicos
          await onSync(filters, orderIds, false);
        }
      }

      toast({
        title: '⏱️ Sincronización iniciada',
        description: `Procesando ${description}... Se mostrará el progreso en tiempo real.`,
        status: 'info',
        duration: 4000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error en sincronización:', error);
      toast({
        title: 'Error en sincronización',
        description: error.message || 'No se pudo iniciar la sincronización',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Función para actualizar boletas - OPTIMIZADA para selección masiva
  const handleBulkUpdateInvoiceNumbers = async () => {
    let orderIds = null;
    let description = '';
    let useFilters = false;

    if (selectAllPages) {
      // Modo selección masiva: usar filtros directamente
      useFilters = true;
      description = `todas las ${payments.total.toLocaleString()} órdenes del filtro actual`;
    } else if (selectedOrders.size > 0) {
      // Con selección específica: usar IDs
      orderIds = Array.from(selectedOrders);
      description = `${orderIds.length} orden${orderIds.length !== 1 ? 'es' : ''} seleccionada${orderIds.length !== 1 ? 's' : ''}`;
    } else {
      // Sin selección: todas las del filtro
      useFilters = true;
      description = 'todas las órdenes del filtro actual';
    }

    const confirmMessage = `¿Estás seguro de que quieres actualizar los números de boleta para ${description}?\n\n⚠️ Solo se procesarán órdenes que tengan BillMySales ID.\nℹ️ El proceso puede tomar varios minutos para grandes volúmenes.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Ejecutar actualización
      if (onUpdateInvoiceNumbers) {
        if (useFilters) {
          // Pasar filtros en lugar de IDs para procesamiento masivo optimizado
          await onUpdateInvoiceNumbers(filters, null, true); // true = use filters
        } else {
          // Usar IDs específicos
          await onUpdateInvoiceNumbers(filters, orderIds, false);
        }
      }

      toast({
        title: '⏱️ Actualización de boletas iniciada',
        description: `Procesando ${description}... Se mostrará el progreso en tiempo real.`,
        status: 'info',
        duration: 4000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error en actualización de boletas:', error);
      toast({
        title: 'Error en actualización de boletas',
        description: error.message || 'No se pudo iniciar la actualización de boletas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Función para acciones por lote
  const handleBulkAction = async (action) => {
    if (selectedOrders.size === 0) {
      toast({
        title: 'Sin selección',
        description: 'Selecciona al menos una orden para continuar.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    const orderIds = Array.from(selectedOrders);
    const orderCount = orderIds.length;

    // Confirmación para acciones masivas
    const actionLabels = {
      'completed': 'completar',
      'pending': 'marcar como pendiente',
      'cancelled': 'cancelar', 
      'trash': 'eliminar'
    };

    const confirmMessage = `¿Estás seguro de que quieres ${actionLabels[action]} ${orderCount} orden${orderCount !== 1 ? 'es' : ''}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsProcessingAction(true);
    setProgressData({
      current: 0,
      total: orderCount,
      currentOrder: null,
      completed: [],
      errors: [],
      percentage: 0,
      status: 'processing'
    });

    try {
      let processed = 0;
      const completed = [];
      const errors = [];

      // Procesar una por una para garantizar hooks completos
      for (const orderId of orderIds) {
        try {
          const currentProgress = processed + 1;
          const percentage = Math.round((currentProgress / orderCount) * 100);
          
          setProgressData(prev => ({
            ...prev,
            current: currentProgress,
            currentOrder: orderId,
            percentage: percentage,
            status: 'processing'
          }));

          let result;
          if (action === 'trash') {
            result = await deleteOrder(orderId);
            // deleteOrder from woo.js returns the order data or throws error
            if (result) {
              completed.push(orderId);
              setProgressData(prev => ({
                ...prev,
                completed: [...prev.completed, orderId]
              }));
            } else {
              errors.push(`Orden #${orderId}: Error al eliminar`);
              setProgressData(prev => ({
                ...prev,
                errors: [...prev.errors, `Orden #${orderId}: Error al eliminar`]
              }));
            }
          } else {
            result = await triggerStatusChange(orderId, action);
            if (result.success) {
              completed.push(orderId);
              setProgressData(prev => ({
                ...prev,
                completed: [...prev.completed, orderId]
              }));
            } else {
              errors.push(`Orden #${orderId}: ${result.message}`);
              setProgressData(prev => ({
                ...prev,
                errors: [...prev.errors, `Orden #${orderId}: ${result.message}`]
              }));
            }
          }

          processed++;

          // Delay entre órdenes para no saturar el servidor
          if (processed < orderCount) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }

        } catch (error) {
          errors.push(`Orden #${orderId}: ${error.message}`);
          processed++;
        }
      }

      // Mostrar resultado y actualizar progreso final
      const successCount = completed.length;
      const errorCount = errors.length;

      // Actualizar progreso a 100% y marcar como completado
      setProgressData(prev => ({
        ...prev,
        current: orderCount,
        percentage: 100,
        status: errorCount === 0 ? 'completed' : 'partially_completed'
      }));

      if (errorCount === 0) {
        toast({
          title: '✅ Acción completada',
          description: `${successCount} orden${successCount !== 1 ? 'es' : ''} ${actionLabels[action]} exitosamente.`,
          status: 'success',
          duration: 5000,
          isClosable: true
        });
      } else {
        toast({
          title: '⚠️ Acción parcialmente completada',
          description: `${successCount} exitosas, ${errorCount} con errores. Revisa el progreso para detalles.`,
          status: 'warning',
          duration: 7000,
          isClosable: true
        });
      }

      // Limpiar selección y refrescar datos
      const emptySet = new Set();
      setSelectedOrders(emptySet);
      setSelectAll(false);
      
      if (onRefresh) {
        await onRefresh();
      }

    } catch (error) {
      console.error('Error en acción por lote:', error);
      toast({
        title: 'Error en acción masiva',
        description: 'No se pudo completar la acción. Inténtalo de nuevo.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsProcessingAction(false);
      
      // Mantener progreso visible por 2 segundos antes de resetear
      setTimeout(() => {
        setProgressData({
          current: 0,
          total: 0,
          currentOrder: null,
          completed: [],
          errors: [],
          percentage: 0,
          status: 'idle'
        });
      }, 2000);
    }
  };

  // Generar rango de páginas para navegación
  const getPageNumbers = () => {
    const total = payments.total_pages;
    const current = pagination.page;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, current - delta);
      i <= Math.min(total - 1, current + delta);
      i++
    ) {
      range.push(i);
    }

    if (current - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < total - 1) {
      rangeWithDots.push('...', total);
    } else if (total > 1) {
      rangeWithDots.push(total);
    }

    return rangeWithDots;
  };

  // Componente de indicador de progreso mejorado
  const ProgressIndicator = () => {
    if (!isProcessingAction || progressData.total === 0) return null;

    const getStatusText = () => {
      switch(progressData.status) {
        case 'processing':
          if (progressData.currentOrder) {
            return `Procesando orden #${progressData.currentOrder}...`;
          }
          return 'Iniciando procesamiento...';
        case 'completed':
          return '✅ Procesamiento completado exitosamente';
        case 'partially_completed':
          return '⚠️ Procesamiento completado con algunos errores';
        default:
          return 'Procesando acciones en lote';
      }
    };

    const getColorScheme = () => {
      if (progressData.status === 'completed') return 'green';
      if (progressData.status === 'partially_completed') return 'orange';
      if (progressData.errors.length > 0 && progressData.completed.length === 0) return 'red';
      if (progressData.errors.length > 0) return 'orange';
      return 'orange';
    };

    return (
      <Box mb={4} p={4} bg="coral.tint" borderRadius="14px" border="1.5px solid" borderColor="coral.soft">
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontSize="sm" fontWeight="800" color="coral.700">
              🔄 {getStatusText()}
            </Text>
            <Text fontSize="sm" color="coral.700" fontWeight="600">
              {progressData.current} / {progressData.total} ({progressData.percentage}%)
            </Text>
          </HStack>

          <Progress
            value={progressData.percentage}
            colorScheme={getColorScheme()}
            size="lg"
            hasStripe
            isAnimated
            borderRadius="8px"
          />

          {/* Estadísticas en tiempo real */}
          <HStack spacing={6} fontSize="sm" justify="center">
            <HStack spacing={1}>
              <Icon as={FaCheck} color="ok.500" />
              <Text color="ink.700" fontWeight="600">
                {progressData.completed.length} exitosas
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Icon as={FaTimes} color="danger.500" />
              <Text color="ink.700" fontWeight="600">
                {progressData.errors.length} errores
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Icon as={FaClock} color="coral.500" />
              <Text color="ink.700" fontWeight="600">
                {progressData.total - progressData.current} restantes
              </Text>
            </HStack>
          </HStack>

          {/* Mostrar último error si existe */}
          {progressData.errors.length > 0 && (
            <Box bg="danger.bg" p={2} borderRadius="8px" border="1px solid" borderColor="danger.500">
              <Text fontSize="xs" color="danger.500" fontWeight="600">
                ⚠️ Último error: {progressData.errors[progressData.errors.length - 1]}
              </Text>
            </Box>
          )}
        </VStack>
      </Box>
    );
  };

  // Componente de acciones móviles
  const MobileBulkActions = () => {
    if (!isMobile) return null;
    
    // Determinar el texto del contador
    const getSelectionText = () => {
      if (selectAllPages) {
        return `🌍 TODAS las ${payments.total.toLocaleString()} órdenes del filtro`;
      } else if (selectedOrders.size > 0) {
        return `${selectedOrders.size} seleccionada${selectedOrders.size !== 1 ? 's' : ''}`;
      } else {
        return 'Acciones masivas';
      }
    };
    
    return (
      <Box mb={4} p={3} bg={selectAllPages ? "mint.bg" : "coral.tint"} borderRadius="12px" border="1px solid" borderColor={selectAllPages ? "mint.ink" : "coral.soft"}>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="800" color={selectAllPages ? "mint.ink" : "coral.700"}>
            {getSelectionText()}
          </Text>
          {(selectedOrders.size > 0 || selectAllPages) && (
            <Button
              size="xs"
              variant="ghost"
              color="ink.400"
              _hover={{ color: 'coral.700', bg: 'coral.soft' }}
              onClick={() => {
                const emptySet = new Set();
                setSelectedOrders(emptySet);
                setSelectAll(false);
                setSelectAllPages(false);
              }}
            >
              Limpiar
            </Button>
          )}
        </HStack>
        <HStack spacing={2} flexWrap="wrap">
          {/* Acciones de cambio de estado - solo para selección específica */}
          {selectedOrders.size > 0 && !selectAllPages && (
            <>
              <Button
                size="sm"
                leftIcon={<Icon as={FaCheck} />}
                bg="ok.500"
                color="white"
                borderRadius="9px"
                _hover={{ opacity: 0.9 }}
                onClick={() => handleBulkAction('completed')}
                isDisabled={isProcessingAction}
              >
                Completar
              </Button>
              <Button
                size="sm"
                leftIcon={<Icon as={FaClock} />}
                bg="warn.500"
                color="white"
                borderRadius="9px"
                _hover={{ opacity: 0.9 }}
                onClick={() => handleBulkAction('pending')}
                isDisabled={isProcessingAction}
              >
                Pendiente
              </Button>
              <Button
                size="sm"
                leftIcon={<Icon as={FaTimes} />}
                bg="danger.500"
                color="white"
                borderRadius="9px"
                _hover={{ opacity: 0.9 }}
                onClick={() => handleBulkAction('cancelled')}
                isDisabled={isProcessingAction}
              >
                Cancelar
              </Button>
            </>
          )}

          {/* Acciones principales - siempre disponibles */}
          <Button
            size="sm"
            leftIcon={<Icon as={FaSync} />}
            bg={selectAllPages ? "coral.500" : "transparent"}
            color={selectAllPages ? "white" : "coral.700"}
            border={selectAllPages ? "none" : "1px solid"}
            borderColor="coral.500"
            borderRadius="9px"
            _hover={{ bg: selectAllPages ? 'coral.600' : 'coral.soft' }}
            onClick={handleBulkSync}
            isDisabled={isProcessingAction}
          >
            {selectAllPages ? `Sync ${payments.total.toLocaleString()}` : 'Sincronizar'}
          </Button>
          <Button
            size="sm"
            leftIcon={<Icon as={FaReceipt} />}
            bg={selectAllPages ? "lav.ink" : "transparent"}
            color={selectAllPages ? "white" : "lav.ink"}
            border={selectAllPages ? "none" : "1px solid"}
            borderColor="lav.ink"
            borderRadius="9px"
            _hover={{ bg: selectAllPages ? 'lav.ink' : 'lav.bg', opacity: selectAllPages ? 0.9 : 1 }}
            onClick={handleBulkUpdateInvoiceNumbers}
            isDisabled={isProcessingAction}
          >
            {selectAllPages ? `Boletas ${payments.total.toLocaleString()}` : 'Boletas'}
          </Button>
        </HStack>
      </Box>
    );
  };

  // Definir captions para la tabla con ordenamiento (Estado movido a primera posición)
  const captions = isMobile ? [] : [
    <VStack key="selectAll" spacing={1} align="start">
      <Checkbox 
        isChecked={selectAll}
        isIndeterminate={selectedOrders.size > 0 && selectedOrders.size < payments.data.length}
        onChange={(e) => handleSelectAll(e.target.checked)}
        isDisabled={isProcessingAction}
        size="sm"
      />
      {selectAll && !selectAllPages && payments.total > payments.data.length && (
        <Button
          size="xs"
          variant="link"
          color="coral.700"
          fontSize="xs"
          p={0}
          h="auto"
          onClick={handleSelectAllPages}
          isDisabled={isProcessingAction}
        >
          Seleccionar todas las {payments.total}
        </Button>
      )}
      {selectAllPages && (
        <Text fontSize="xs" color="mint.ink" fontWeight="700">
          🌍 TODAS las {payments.total.toLocaleString()}
        </Text>
      )}
    </VStack>,
    <HStack key="estado" cursor="pointer" onClick={() => handleSortChange('status')}>
      <Text>Estado</Text>
      {renderSortIcon('status')}
    </HStack>,
    <HStack key="mes" cursor="pointer" onClick={() => handleSortChange('mes')}>
      <Text>Mes</Text>
      {renderSortIcon('mes')}
    </HStack>,
    <HStack key="alumno" cursor="pointer" onClick={() => handleSortChange('customer_name')}>
      <Text>Alumno</Text>
      {renderSortIcon('customer_name')}
    </HStack>,
    <HStack key="apoderado" cursor="pointer">
      <Text>Apoderado</Text>
    </HStack>,
    <HStack key="fecha_pago" cursor="pointer" onClick={() => handleSortChange('date_paid')}>
      <Text>Fecha de Pago</Text>
      {renderSortIcon('date_paid')}
    </HStack>,
    <HStack key="fecha_actualizacion" cursor="pointer" onClick={() => handleSortChange('date_modified')}>
      <Text>Actualización</Text>
      {renderSortIcon('date_modified')}
    </HStack>,
    <HStack key="medio_pago" cursor="pointer">
      <Text>Medio Pago</Text>
    </HStack>,
    <HStack key="boleta_info" cursor="pointer">
      <Text>Boleta</Text>
    </HStack>
  ];

  if (!payments.data || payments.data.length === 0) {
    return (
      <Box bg="white" borderRadius="16px" border="1px solid" borderColor="cream.line" p={4} fontFamily="jakarta">
        <Text fontSize='lg' color={textColor} fontWeight='800' mb={3}>
          Historial de Pagos
        </Text>
        <Alert status="info" borderRadius="10px">
          <AlertIcon />
          No se encontraron pagos con los filtros aplicados.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <ModalOrderEdit
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={modalOrder}
        action={'view'}
        customer={modalCustomer}
      />

      <Box bg="white" borderRadius="16px" border="1px solid" borderColor="cream.line" p={4} fontFamily="jakarta">
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={4}>
          <Box>
            <Text fontSize='lg' color={textColor} fontWeight='800' letterSpacing="-0.01em">
              Historial de Pagos
            </Text>
            <Text fontSize="sm" color="ink.400">
              {payments.total} resultado{payments.total !== 1 ? 's' : ''} encontrado{payments.total !== 1 ? 's' : ''}
              {selectedOrders.size > 0 && (
                <Text as="span" color="coral.700" fontWeight="700" ml={2}>
                  • {selectedOrders.size} seleccionada{selectedOrders.size !== 1 ? 's' : ''}
                </Text>
              )}
            </Text>
          </Box>

          <HStack spacing={3}>
            {/* Menú de acciones por lote (Desktop) */}
            {!isMobile && (
              <Menu>
                <MenuButton
                  as={Button}
                  bg="coral.500"
                  color="white"
                  fontWeight="600"
                  borderRadius="10px"
                  _hover={{ bg: 'coral.600' }}
                  _active={{ bg: 'coral.700' }}
                  size="sm"
                  isDisabled={isProcessingAction}
                >
                  {selectedOrders.size > 0 ? `Acciones (${selectedOrders.size})` : 'Acciones (Todas)'}
                </MenuButton>
                <MenuList>
                  {selectedOrders.size > 0 && (
                    <>
                      <MenuItem
                        icon={<Icon as={FaCheck} color="ok.500" />}
                        onClick={() => handleBulkAction('completed')}
                      >
                        ✅ Marcar como Completado
                      </MenuItem>
                      <MenuItem
                        icon={<Icon as={FaClock} color="warn.500" />}
                        onClick={() => handleBulkAction('pending')}
                      >
                        🕐 Marcar como Pendiente
                      </MenuItem>
                      <MenuItem
                        icon={<Icon as={FaTimes} color="danger.500" />}
                        onClick={() => handleBulkAction('cancelled')}
                      >
                        ❌ Cancelar Órdenes
                      </MenuItem>

                      {/* Separador solo si hay acciones de estado */}
                      <Box height="1px" bg="cream.line" my={2} mx={3} />
                    </>
                  )}

                  <MenuItem
                    icon={<Icon as={FaSync} color="sky.ink" />}
                    onClick={handleBulkSync}
                    isDisabled={isProcessingAction}
                  >
                    🔄 Sincronizar Órdenes
                  </MenuItem>
                  <MenuItem
                    icon={<Icon as={FaReceipt} color="lav.ink" />}
                    onClick={handleBulkUpdateInvoiceNumbers}
                    isDisabled={isProcessingAction}
                  >
                    🧾 Actualizar Boletas
                  </MenuItem>

                  {selectedOrders.size > 0 && (
                    <>
                      {/* Separador */}
                      <Box height="1px" bg="cream.line" my={2} mx={3} />

                      <MenuItem
                        icon={<Icon as={FaTrash} color="ink.400" />}
                        onClick={() => handleBulkAction('trash')}
                      >
                        🗑️ Mover a Papelera
                      </MenuItem>
                    </>
                  )}
                </MenuList>
              </Menu>
            )}

            {/* Botón de actualizar */}
            <Button
              leftIcon={<Icon as={FaSync} />}
              bg="cream.bg2"
              color="ink.700"
              borderRadius="10px"
              _hover={{ bg: 'coral.soft', color: 'coral.700' }}
              size="sm"
              onClick={() => {
                if (onRefresh) onRefresh();
              }}
              isLoading={isLoading}
              loadingText="Actualizando..."
            >
              Actualizar
            </Button>

            {/* Opciones de exportación simplificadas */}
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<Icon as={FaDownload} />}
                bg="transparent"
                border="1px solid"
                borderColor="coral.500"
                color="coral.700"
                borderRadius="10px"
                _hover={{ bg: 'coral.tint' }}
                size="sm"
                isLoading={isExporting || isExportingOverdue}
                loadingText={isExportingOverdue ? "Generando morosos..." : "Exportando..."}
                isDisabled={isExporting || isExportingOverdue}
              >
                {selectedOrders.size > 0 ? `Exportar (${selectedOrders.size})` : 'Exportar'}
              </MenuButton>
              <MenuList>
                {/* Exportación de resultados actuales */}
                <MenuItem
                  icon={<Icon as={FaFileExcel} color="ok.500" />}
                  onClick={() => handleExport('excel')}
                  isDisabled={isExporting || isExportingOverdue}
                >
                  📊 Resultados actuales ({selectedOrders.size > 0 ? selectedOrders.size : payments.total})
                </MenuItem>

                <MenuItem
                  icon={<Icon as={FaFileExcel} color="amber.ink" />}
                  onClick={() => handleQuickExport('today')}
                  isDisabled={isExporting || isExportingOverdue}
                >
                  📅 Pagos de hoy
                </MenuItem>

                <MenuItem
                  icon={<Icon as={FaFileExcel} color="ink.400" />}
                  onClick={() => handleQuickExport('yesterday')}
                  isDisabled={isExporting || isExportingOverdue}
                >
                  📅 Pagos de ayer
                </MenuItem>

                {/* Separador */}
                <Box height="1px" bg="cream.line" my={2} mx={3} />

                {/* Exportar morosos */}
                {(() => {
                  const period = getOverduePeriod();
                  return (
                    <MenuItem
                      icon={<Icon as={FaExclamationTriangle} color="danger.500" />}
                      onClick={handleExportOverdue}
                      isDisabled={isExporting || isExportingOverdue}
                      _hover={{ bg: "danger.bg" }}
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">
                          {isExportingOverdue ? "⏳ Generando..." : "⚠️ Listado de morosos"}
                        </Text>
                        <Text fontSize="xs" color={period.isCustom ? "sky.ink" : "ink.400"}>
                          {period.isCustom ? `🔍 Filtro aplicado: ${period.label}` : `📅 Año escolar: ${period.label}`}
                        </Text>
                      </VStack>
                    </MenuItem>
                  );
                })()}
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        {/* Indicador de progreso */}
        <ProgressIndicator />

        {/* Acciones móviles */}
        <MobileBulkActions />
        
        <Box
          position="relative"
          minHeight="200px"
          borderRadius="16px"
          overflowX={isMobile ? "hidden" : "auto"}
          className={!isMobile ? "horizontal-scroll" : ""}
          opacity={isLoading ? 0.6 : 1}
          transition="opacity 0.2s"
        >
          <Table variant='simple' color={textColor} size="sm">
            {!isMobile && (
              <Thead bg="cream.bg2">
                <Tr height="40px">
                  {captions.map((caption, idx) => (
                    <Th border="none" color='ink.400' key={idx} fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.03em" py={2}>
                      {caption}
                    </Th>
                  ))}
                </Tr>
              </Thead>
            )}
            <Tbody>
              {payments.data.map((row) => (
                <PaymentTableRow
                  key={row.id}
                  {...row}
                  onClick={(event) => handleRowClick(row, event)}
                  isSelected={selectedOrders.has(row.id)}
                  onSelect={(isChecked, event) => handleSelectOrder(row.id, isChecked, event)}
                  showCheckbox={true}
                />
              ))}
            </Tbody>
          </Table>

          {/* Paginación */}
          <Flex
            justify="space-between"
            align="center"
            mt={4}
            flexWrap="wrap"
            gap={3}
          >
            {/* Elementos por página */}
            <HStack spacing={2}>
              <Text fontSize="sm" color={textColor}>Mostrar:</Text>
              <Select
                size="sm"
                value={pagination.per_page}
                onChange={(e) => handlePerPageChange(e.target.value)}
                width="auto"
                bg="cream.bg"
                borderColor="cream.line2"
                borderRadius="8px"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </Select>
              <Text fontSize="sm" color={textColor}>resultados</Text>
            </HStack>

            {/* Información de paginación */}
            <Text fontSize="sm" color="ink.400">
              Mostrando {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, payments.total)} de {payments.total}
            </Text>

            {/* Navegación de páginas */}
            <HStack spacing={1}>
              <IconButton
                size="sm"
                icon={<Icon as={FaChevronLeft} />}
                onClick={() => handlePageChange(pagination.page - 1)}
                isDisabled={pagination.page <= 1 || isLoading}
                bg="cream.bg2"
                color="ink.700"
                borderRadius="8px"
                _hover={{ bg: 'coral.soft', color: 'coral.700' }}
              />

              {getPageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                  <Text key={`dots-${index}`} px={2} color="ink.400">...</Text>
                ) : (
                  <Button
                    key={`page-${pageNum}`}
                    size="sm"
                    borderRadius="8px"
                    bg={pagination.page === pageNum ? "coral.500" : "cream.bg2"}
                    color={pagination.page === pageNum ? "white" : "ink.700"}
                    _hover={{ bg: pagination.page === pageNum ? 'coral.600' : 'coral.soft', color: pagination.page === pageNum ? 'white' : 'coral.700' }}
                    onClick={() => handlePageChange(pageNum)}
                    isDisabled={isLoading}
                  >
                    {pageNum}
                  </Button>
                )
              ))}

              <IconButton
                size="sm"
                icon={<Icon as={FaChevronRight} />}
                onClick={() => handlePageChange(pagination.page + 1)}
                isDisabled={pagination.page >= payments.total_pages || isLoading}
                bg="cream.bg2"
                color="ink.700"
                borderRadius="8px"
                _hover={{ bg: 'coral.soft', color: 'coral.700' }}
              />
            </HStack>
          </Flex>
        </Box>
      </Box>
    </>
  );
};

export default PaymentTable;